import { useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";

/**
 * Custom hook for managing WebSocket connection to job status updates.
 * 
 * @param {string} jobId - The job ID to subscribe to
 * @param {object} options - Configuration options
 *   - onStatusUpdate: Callback when status updates are received
 *   - onComplete: Callback when job completes successfully
 *   - onError: Callback when job fails
 *   - autoConnect: Whether to connect automatically (default: true)
 * @returns {object} - { status, progress, progressMessage, result, error, connected }
 */
export function useJobStatus(jobId, options = {}) {
  const {
    onStatusUpdate,
    onComplete,
    onError,
    autoConnect = true,
    timeoutMs = 90000,
  } = options;

  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const socketRef = useRef(null);
  const timeoutRef = useRef(null);
  const terminalRef = useRef(false); // Guard against double-firing callbacks
  const pollRef = useRef(null);

  useEffect(function connectWebSocketAndPollJob() {
    if (!jobId || !autoConnect) {
      return;
    }

    terminalRef.current = false;
    setTimedOut(false);

    const startTimeout = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setTimedOut(true);
        if (onError) {
          onError("Job timed out — no updates received for " + (timeoutMs / 1000) + "s");
        }
      }, timeoutMs);
    };

    // Process a job update (from WS or poll), guarding against duplicate terminal events
    const processUpdate = (data) => {
      console.log("useJobStatus processUpdate:", data.status, "terminal:", terminalRef.current, "hasResult:", !!data.result, "hasOnComplete:", !!onComplete);
      if (terminalRef.current) return;

      if (data.status) setStatus(data.status);
      if (data.progress !== undefined) setProgress(data.progress);
      if (data.progressMessage !== undefined) setProgressMessage(data.progressMessage);
      if (data.result !== undefined) setResult(data.result);
      if (data.error !== undefined) setError(data.error);

      if (onStatusUpdate) {
        onStatusUpdate(data);
      }

      if (data.status === "completed") {
        terminalRef.current = true;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (pollRef.current) clearInterval(pollRef.current);
        console.log("useJobStatus calling onComplete, result keys:", data.result ? Object.keys(data.result) : "null");
        if (onComplete) onComplete(data.result);
      }

      if (data.status === "failed") {
        terminalRef.current = true;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (pollRef.current) clearInterval(pollRef.current);
        if (onError) onError(data.error);
      }
    };

    startTimeout();

    // Polling fallback: catches jobs that complete before the WS connects.
    // Polls at 3s, then every 10s. Stops on any terminal status.
    const pollJobStatus = async () => {
      if (terminalRef.current) return;
      try {
        const response = await fetch(`/api/jobs/${jobId}`);
        if (!response.ok) return;
        const data = await response.json();
        if (data.status === "completed" || data.status === "failed") {
          console.log("Poll caught terminal job status:", data.status);
          processUpdate(data);
        } else {
          // Reset timeout — the job is still alive on the server
          startTimeout();
        }
      } catch (e) {
        // Polling is best-effort
      }
    };

    // First poll after 3s, then every 10s
    const firstPollTimer = setTimeout(() => {
      pollJobStatus();
      pollRef.current = setInterval(pollJobStatus, 10000);
    }, 3000);

    // Connect to WebSocket server
    const socket = io(window.location.origin, {
      transports: ["websocket", "polling"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("WebSocket connected");
      setConnected(true);

      // Subscribe to job updates
      socket.emit("subscribeJob", jobId);
    });

    socket.on("disconnect", () => {
      console.log("WebSocket disconnected");
      setConnected(false);
    });

    socket.on("jobUpdate", (data) => {
      console.log("Job update received:", data);

      // Reset timeout on every update
      startTimeout();

      processUpdate(data);
    });

    socket.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error);
      setConnected(false);
    });

    // Cleanup on unmount
    return () => {
      clearTimeout(firstPollTimer);
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (socket) {
        socket.disconnect();
      }
    };
  }, [jobId, autoConnect]);

  return {
    status,
    progress,
    progressMessage,
    result,
    error,
    connected,
    timedOut,
  };
}




